import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { postHelpChat } from "@/api/helpChatApi";
import { helpItemAnswerKey, helpItemQuestionKey } from "@/content/helpFaq";
import { resolveApiErrorFromException } from "@/lib/apiErrorHandling";

export type HelpMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
};

type OpenHelpChatOptions = {
  faqId?: string;
  browseAll?: boolean;
};

type HelpContextValue = {
  chatOpen: boolean;
  browseAll: boolean;
  asking: boolean;
  messages: HelpMessage[];
  openChat: (options?: OpenHelpChatOptions) => void;
  closeChat: () => void;
  startNewChat: () => void;
  ask: (question: string) => Promise<void>;
};

const HelpContext = createContext<HelpContextValue | null>(null);

function nextMessageId(): string {
  return crypto.randomUUID();
}

function helpLocale(language: string): "pt-BR" | "en" {
  return language.toLowerCase().startsWith("pt") ? "pt-BR" : "en";
}

export function HelpProvider({ children }: { children: ReactNode }): ReactElement {
  const { t, i18n } = useTranslation();
  const [chatOpen, setChatOpen] = useState(false);
  const [browseAll, setBrowseAll] = useState(false);
  const [asking, setAsking] = useState(false);
  const [messages, setMessages] = useState<HelpMessage[]>([]);

  const ask = useCallback(
    async (question: string) => {
      const trimmed = question.trim();
      if (trimmed.length === 0 || asking) return;

      const userMessage: HelpMessage = { id: nextMessageId(), role: "user", text: trimmed };
      const history = [...messages, userMessage];
      setMessages(history);
      setBrowseAll(false);
      setChatOpen(true);
      setAsking(true);

      try {
        const { reply } = await postHelpChat({
          locale: helpLocale(i18n.language),
          messages: history.map((message) => ({
            role: message.role,
            content: message.text,
          })),
        });
        setMessages((current) => [
          ...current,
          { id: nextMessageId(), role: "assistant", text: reply },
        ]);
      } catch (err) {
        setMessages((current) => [
          ...current,
          {
            id: nextMessageId(),
            role: "assistant",
            text: resolveApiErrorFromException(err),
          },
        ]);
      } finally {
        setAsking(false);
      }
    },
    [asking, i18n.language, messages],
  );

  const openChat = useCallback(
    (options?: OpenHelpChatOptions) => {
      if (options?.faqId) {
        const question = t(helpItemQuestionKey(options.faqId));
        setMessages([
          { id: nextMessageId(), role: "user", text: question },
          { id: nextMessageId(), role: "assistant", text: t(helpItemAnswerKey(options.faqId)) },
        ]);
        setBrowseAll(false);
        setChatOpen(true);
        return;
      }
      if (options?.browseAll) {
        setMessages([]);
        setBrowseAll(true);
      }
      setChatOpen(true);
    },
    [t],
  );

  const closeChat = useCallback(() => {
    setChatOpen(false);
  }, []);

  const startNewChat = useCallback(() => {
    setMessages([]);
    setBrowseAll(false);
    setAsking(false);
    setChatOpen(true);
  }, []);

  const value = useMemo(
    (): HelpContextValue => ({
      chatOpen,
      browseAll,
      asking,
      messages,
      openChat,
      closeChat,
      startNewChat,
      ask,
    }),
    [ask, asking, browseAll, chatOpen, closeChat, messages, openChat, startNewChat],
  );

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

export function useHelp(): HelpContextValue {
  const context = useContext(HelpContext);
  if (!context) {
    throw new Error("useHelp must be used within HelpProvider");
  }
  return context;
}
